import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import type { MediaSettings } from '@workspace/seedforge-ui/pitch'
import { PitchPendingState, PreJoinScreen } from '@workspace/seedforge-ui/pitch'
import { Loader2, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FounderLayout } from '../../../../components/founder-layout'
import { useStartupContext } from '../../../../contexts/startup-guard'
import { FeedbackDashboard } from '../../../../features/feedback/components/feedback-dashboard'
import { PitchRoom } from '../../../../features/pitch/components/pitch-room'
import { PostImprovementPromptOverlay } from '../../../../features/post-improvement-prompt/components/post-improvement-prompt-overlay'
import { usePostImprovementPrompt } from '../../../../features/post-improvement-prompt/hooks/use-post-improvement-prompt'
import { usePitchQuery, useShareLinksQuery, useStartPitchMutation } from '../../../../hooks/api'
import { useBasePath } from '../../../../hooks/use-base-path'
import { useWorkspaceReady } from '../../../../hooks/use-workspace-ready'
import { useSession } from '../../../../lib/auth'

export const Route = createFileRoute('/app/startup/$id/pitch')({
  component: GuardedPitchPage,
})

function GuardedPitchPage() {
  const { startup, pitchDeckStatus } = useStartupContext()
  const { isResolved } = useWorkspaceReady()

  // Only require a deck — not matches — to access Feedback.
  if (!isResolved) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="size-10 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (pitchDeckStatus === 'NONE') {
    return <Navigate to="/app/startup/$id" params={{ id: startup.id }} replace />
  }

  return <PitchPage />
}

function PitchPage() {
  const { startup } = useStartupContext()
  const { data: session } = useSession()
  const navigate = useNavigate()
  const basePath = useBasePath()

  const [mediaSettings, setMediaSettings] = useState<MediaSettings>({
    audioEnabled: true,
    videoEnabled: true,
  })
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [sessionJustEnded, setSessionJustEnded] = useState(false)
  const [showPostImprovementPrompt, setShowPostImprovementPrompt] = useState(false)
  const { canShowToday, markShownToday } = usePostImprovementPrompt(startup)
  const { data: shareLinksData } = useShareLinksQuery(startup.id)
  const defaultShareLink =
    shareLinksData?.data?.find((l) => l.isDefault) ?? shareLinksData?.data?.[0]
  const shareUrl = defaultShareLink ? `${window.location.origin}/s/${defaultShareLink.token}` : null
  const { data: pitchOverview, isPending } = usePitchQuery(startup.id, {
    refetchInterval: (query) => {
      const status = query.state.data?.data?.ongoing?.status
      if (status === 'PENDING') return 3000
      return false
    },
  })

  const ongoingSession = pitchOverview?.data?.ongoing ?? null
  const status = ongoingSession?.status
  const userInitial = session?.user?.name?.charAt(0).toUpperCase() ?? '?'

  const startPitch = useStartPitchMutation(startup.id)

  const hasRedirectedRef = useRef(false)
  useEffect(() => {
    if (hasRedirectedRef.current) return
    const shouldRedirect =
      status === 'FAILED' ||
      status === 'INCOMPLETE' ||
      (status === 'COMPLETED' && !sessionJustEnded)
    if (shouldRedirect) {
      hasRedirectedRef.current = true
      navigate({ to: basePath, replace: true })
    }
  }, [status, navigate, basePath, sessionJustEnded])

  useEffect(() => {
    if (!sessionJustEnded || hasRedirectedRef.current) return
    const timer = setTimeout(() => {
      hasRedirectedRef.current = true
      if (shareUrl && canShowToday()) {
        markShownToday()
        setShowPostImprovementPrompt(true)
        return
      }
      navigate({ to: basePath, replace: true })
    }, 5000)
    return () => clearTimeout(timer)
  }, [sessionJustEnded, navigate, basePath, shareUrl, canShowToday, markShownToday])

  const handleJoinSession = useCallback(
    async (settings: MediaSettings) => {
      if (!ongoingSession?.id) return
      setIsJoining(true)
      setJoinError(null)
      setMediaSettings(settings)
      try {
        await startPitch.mutateAsync({
          path: { pitchId: ongoingSession.id },
        })
      } catch (err) {
        setJoinError(err instanceof Error ? err.message : 'Failed to join session')
        setIsJoining(false)
      }
    },
    [ongoingSession?.id, startPitch]
  )

  if (sessionJustEnded) {
    if (showPostImprovementPrompt && shareUrl) {
      return (
        <PostImprovementPromptOverlay
          reasons={['AI session completed']}
          shareUrl={shareUrl}
          onDismiss={() => navigate({ to: basePath, replace: true })}
        />
      )
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-emerald-400/10">
            <Sparkles className="size-6 text-emerald-400" />
          </div>
          <h1 className="mt-6 text-xl font-semibold text-foreground">Great session!</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            We're running your evaluation now. This usually takes 2–5 minutes. We'll notify you when
            it's ready.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground/50">
            <div className="relative size-2">
              <div className="absolute size-2 animate-ping rounded-full bg-[#f97316]/50" />
              <div className="relative size-2 rounded-full bg-[#f97316]" />
            </div>
            Redirecting to dashboard...
          </div>
        </div>
      </div>
    )
  }

  if (status === 'IN_PROGRESS') {
    return (
      <PitchRoom
        sessionId={ongoingSession?.id ?? ''}
        sessionToken={ongoingSession?.sessionToken ?? ''}
        pitchDeckId={ongoingSession?.pitchDeckId ?? null}
        livekitUrl={import.meta.env.VITE_LIVEKIT_URL}
        mediaSettings={mediaSettings}
        onSessionEnd={() => setSessionJustEnded(true)}
      />
    )
  }

  if (status === 'PENDING') {
    return (
      <FounderLayout activeNav="">
        <div className="mx-auto max-w-2xl py-4">
          <PitchPendingState
            preparationStep={
              ongoingSession?.preparationStep as
                | 'ANALYZING_DECK'
                | 'RESEARCHING'
                | 'PREPARING_QUESTIONS'
                | undefined
            }
            createdAt={ongoingSession?.createdAt ?? new Date()}
          />
        </div>
      </FounderLayout>
    )
  }

  if (status === 'ACTIVE') {
    return (
      <FounderLayout activeNav="">
        <div className="mx-auto max-w-2xl py-4">
          <PreJoinScreen
            userInitial={userInitial}
            onJoin={handleJoinSession}
            isLoading={isJoining}
            error={joinError}
          />
        </div>
      </FounderLayout>
    )
  }

  return (
    <FounderLayout activeNav="">
      {!isPending && (
        <FeedbackDashboard
          startupId={startup.id}
          onStartSession={() => navigate({ to: `${basePath}/pitch/start` })}
        />
      )}
    </FounderLayout>
  )
}
